/**
 * Minimal "copy to clipboard" icon button used in the v2 concept-detail
 * sidebar header. Sits beside the concept name and GFC so the user can
 * grab either value with one click.
 *
 * Visual brief:
 *   - No background at idle — just the icon.
 *   - Soft brand-purple wash on hover, scoped to the icon's footprint.
 *   - On copy success the icon swaps from `Copy` to `Check` for ~1.5s
 *     and the button colour shifts to a confirmation green so the
 *     user gets a clear "it worked" cue without a popup or toast.
 *   - Each instance manages its own confirmation state, so copying
 *     one doesn't reset the other's.
 *
 * The clipboard call uses the modern async API; older browsers fall
 * through to a textarea-based fallback so users don't see a silent
 * failure on intranet machines that haven't migrated.
 */

import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface CopyButtonProps {
  /** Value to put on the clipboard. Stringified before write. */
  value: string | number;
  /** Accessible label, also used as the title tooltip. */
  ariaLabel: string;
  /** Confirmation duration in ms. Defaults to 1500. */
  feedbackMs?: number;
}

const BRAND_PURPLE = '#522d6e';
// Whisper-light wash — barely-there backdrop so the icon reads as
// the primary cue and the surface beneath stays dominant.
const BRAND_PURPLE_TINT = 'rgba(82, 45, 110, 0.025)';
// Lighter idle gray — was #6b7280 which felt heavy next to the
// section label. #9ca3af pairs better with the small 14px icon.
const IDLE_GRAY = '#9ca3af';
const CONFIRM_GREEN = '#15803d';

const writeToClipboard = async (text: string): Promise<boolean> => {
  // Preferred path: async clipboard API. Requires HTTPS or localhost.
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to legacy path on permission errors.
    }
  }
  // Fallback: hidden textarea + execCommand. Works in older Edge /
  // Safari and on insecure-context intranet pages where the async
  // API is gated.
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(textarea);
  return ok;
};

const CopyButton = ({
  value,
  ariaLabel,
  feedbackMs = 1500,
}: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending feedback timer on unmount so we don't poke
  // setState on an unmounted component (e.g. user closes the sidebar
  // mid-confirmation).
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const handleClick = async (e: React.MouseEvent) => {
    // Stop propagation so a click on the copy button doesn't bubble
    // up to any parent (e.g. a clickable header row) and trigger
    // navigation or selection.
    e.stopPropagation();
    e.preventDefault();
    const ok = await writeToClipboard(String(value));
    if (!ok) return;
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), feedbackMs);
  };

  const colour = copied ? CONFIRM_GREEN : IDLE_GRAY;

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={ariaLabel}
      title={copied ? 'Copied' : ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        padding: 4,
        margin: 0,
        cursor: 'pointer',
        borderRadius: 6,
        color: colour,
        // Smooth handoff between idle / hover / confirm states.
        transition:
          'color 0.18s ease, background-color 0.18s ease, transform 0.12s ease',
        flexShrink: 0,
      }}
      onMouseEnter={e => {
        if (copied) return;
        e.currentTarget.style.background = BRAND_PURPLE_TINT;
        e.currentTarget.style.color = BRAND_PURPLE;
      }}
      onMouseLeave={e => {
        if (copied) return;
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = colour;
      }}
      onMouseDown={e => {
        e.currentTarget.style.transform = 'scale(0.92)';
      }}
      onMouseUp={e => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
    >
      {copied ? (
        <Check size={14} strokeWidth={2.5} aria-hidden />
      ) : (
        <Copy size={14} strokeWidth={2} aria-hidden />
      )}
    </button>
  );
};

export default CopyButton;
